import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Image, { StaticImageData } from 'next/image'

export type LogosGridVariant = 'flex' | 'grid'

type LogoItem = {
    name: string
    img: StaticImageData
    width: number
    height: number
}

function FlexLogosGrid({ logos, sx }: { logos: LogoItem[]; sx?: SxProps }) {
    return (
        <Box
            sx={{
                width: `100%`,
                display: 'flex',
                justifyContent: {
                    xs: 'start',
                    sm: 'start',
                    md: 'space-between',
                    lg: 'space-between',
                    xl: 'space-between',
                },
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: {
                    xs: 7,
                    sm: 7,
                    md: 0,
                    lg: 0,
                    xl: 0,
                },
                rowGap: 5,
                ...sx,
            }}
        >
            {logos.map(({ img, name, width, height }, index) => (
                <Box
                    key={index}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: {
                            xs: 'auto',
                            sm: 'auto',
                            md: `auto`,
                            lg: `${100 / logos.length}%`,
                            xl: `${100 / logos.length}%`,
                        },
                        justifyContent: 'center',
                    }}
                >
                    <Image src={img} alt={`${name} logo`} width={width} height={height} />
                </Box>
            ))}
        </Box>
    )
}

function GridLogosGrid({
    logos,
    hiddenOnSmIndices,
    sx,
}: {
    logos: LogoItem[]
    hiddenOnSmIndices?: number[]
    sx?: SxProps
}) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(3, 1fr)',
                },
                gap: 5,
                ...sx,
            }}
        >
            {logos.map(({ img, name, width, height }, index) => (
                <Box
                    key={index}
                    sx={{
                        display: {
                            xs: 'flex',
                            sm: hiddenOnSmIndices?.includes(index) ? 'none' : 'flex',
                        },
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Image src={img} alt={`${name} logo`} width={width} height={height} />
                </Box>
            ))}
        </Box>
    )
}

const LogosGrid = ({
    logos,
    variant = 'flex',
    hiddenOnSmIndices,
    sx,
}: {
    logos: LogoItem[]
    variant?: LogosGridVariant
    hiddenOnSmIndices?: number[]
    sx?: SxProps
}) =>
    variant === 'grid' ? (
        <GridLogosGrid logos={logos} hiddenOnSmIndices={hiddenOnSmIndices} sx={sx} />
    ) : (
        <FlexLogosGrid logos={logos} sx={sx} />
    )

export default LogosGrid
